<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import {
  MAP_STYLE_OPTIONS,
  resolveMapStyle,
} from "./map-style-utils";

const props = defineProps({
  modelValue: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["change"]);

const rootRef = ref(null);
const isOpen = ref(false);
const selected = ref(resolveMapStyle(props.modelValue));

const selectedOption = computed(() =>
  MAP_STYLE_OPTIONS.find((item) => item.value === selected.value) || MAP_STYLE_OPTIONS[0]
);

function closeMenu() {
  isOpen.value = false;
}

function toggleMenu() {
  isOpen.value = !isOpen.value;
}

function selectStyle(style) {
  const value = resolveMapStyle(style);
  selected.value = value;
  isOpen.value = false;
  emit("change", value);
}

watch(
  () => props.modelValue,
  (value) => {
    selected.value = resolveMapStyle(value);
  }
);

function onWindowPointerDown(event) {
  if (!isOpen.value) return;
  if (rootRef.value?.contains(event.target)) return;
  closeMenu();
}

function onKeydown(event) {
  if (event.key === "Escape") {
    closeMenu();
  }
}

onMounted(() => {
  window.addEventListener("pointerdown", onWindowPointerDown);
  window.addEventListener("keydown", onKeydown);
});

onBeforeUnmount(() => {
  window.removeEventListener("pointerdown", onWindowPointerDown);
  window.removeEventListener("keydown", onKeydown);
});
</script>

<template>
  <div ref="rootRef" class="map-style-dropdown">
    <button
      v-tooltip="selectedOption.label"
      type="button"
      class="map-style-btn"
      :aria-label="`Tipo de mapa: ${selectedOption.label}`"
      :aria-expanded="isOpen ? 'true' : 'false'"
      @click="toggleMenu"
    >
      <i :class="selectedOption.icon" aria-hidden="true"></i>
    </button>

    <div v-if="isOpen" class="map-style-menu">
      <button
        v-for="opt in MAP_STYLE_OPTIONS"
        :key="opt.value"
        type="button"
        class="map-style-menu__item"
        :class="{ 'map-style-menu__item--active': opt.value === selected }"
        @click="selectStyle(opt.value)"
      >
        <i :class="opt.icon" class="map-style-menu__icon" aria-hidden="true"></i>
        {{ opt.label }}
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.map-style-dropdown {
  position: relative;
}

.map-style-btn {
  align-items: center;
  background: #ffffff;
  border: 1px solid rgba(203, 213, 225, 0.92);
  border-radius: 10px;
  color: #182126;
  cursor: pointer;
  display: inline-flex;
  font-size: 14px;
  height: 34px;
  justify-content: center;
  padding: 0;
  transition:
    background-color 180ms ease,
    border-color 180ms ease;
  width: 34px;
}

.map-style-btn:hover {
  background: #f8fafc;
  border-color: rgba(203, 213, 225, 0.98);
}

.map-style-btn:focus-visible {
  outline: none;
}

.map-style-menu {
  background: #ffffff;
  border: 1px solid rgba(203, 213, 225, 0.92);
  border-radius: 10px;
  bottom: calc(100% + 6px);
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 5px;
  position: absolute;
  right: 0;
  width: max-content;
  z-index: 20;
}

.map-style-menu__item {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 7px;
  color: #344767;
  cursor: pointer;
  display: flex;
  font-size: 0.78rem;
  font-weight: 500;
  gap: 8px;
  line-height: 1.4;
  padding: 6px 10px;
  text-align: left;
  transition:
    background-color 140ms ease,
    color 140ms ease;
  white-space: nowrap;
}

.map-style-menu__item:hover {
  background-color: #f1f5f9;
}

.map-style-menu__item--active {
  background-color: #f1f5f9;
  color: #0f172a;
  font-weight: 600;
}

.map-style-menu__icon {
  color: var(--color-text-secondary);
  font-size: 0.72rem;
  width: 14px;
  text-align: center;
}

.map-style-menu__item--active .map-style-menu__icon {
  color: #0f172a;
}
</style>
